import {
  Alert,
  Box,
  ContentBlock,
  Heading,
  Loader,
  Stack,
  Text,
} from 'braid-design-system';
import React, { type FC, useState, useEffect } from 'react';

import { useEnvironment } from 'src/shared/hooks';

import { ButtonTracking } from '../../../../../components/ButtonTracking/ButtonTracking';
import getJobDetails from '../../graphQL/getJobDetails';

import Phones from './image/phones.png';

import * as styles from './PageIntro.css';

const PageIntro: FC<{ jobAdId: string }> = ({ jobAdId }) => {
  const [jobTitle, setJobTitle] = useState<string>('');
  const [jobExpired, setJobExpired] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const environment = useEnvironment();

  const createQRCodeButtonClickHandler = () => {
    window.open(`/products/qr-code?jobadid=${jobAdId}&displayqrcode=true`);
  };

  useEffect(() => {
    (async () => {
      const { data } = await getJobDetails(jobAdId);
      if (data && data.jobDetails) {
        const {
          job: { title, isExpired },
        } = data.jobDetails;
        setLoading(false);
        setJobTitle(title);
        setJobExpired(isExpired);
      } else {
        setLoading(false);
        setError(true);
      }
    })();
  }, [jobAdId, environment]);

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const renderBodyWithJobTitle = (jobTitle: string) => (
    <Box style={{ maxWidth: '677px' }}>
      <Stack space="xlarge">
        <Heading level="3" align="center" weight="weak">
          The QR code poster for your{' '}
          <span style={{ color: '#E60278', fontWeight: 'bold' }}>
            {jobTitle}
          </span>{' '}
          role is ready to go! Use your personalised job ad QR code poster in
          your shopfront to help more candidates apply.
        </Heading>
        <Box display={['block', 'flex', 'flex']} justifyContent="center">
          <Box display={['block', 'inlineBlock', 'inlineBlock']}>
            <ButtonTracking
              tracking="create_qr_code_pressed"
              aria-label="create-qr-code-button-clicked"
              onClick={createQRCodeButtonClickHandler}
            >
              Create QR code poster
            </ButtonTracking>
          </Box>
        </Box>
      </Stack>
    </Box>
  );

  const renderInvalidJobErrorJSX = (
    <Alert tone="critical">
      <Text>
        Something went wrong, we can’t create your QR code poster right now.
      </Text>
    </Alert>
  );

  const renderExpiredJobErrorJSX = (
    <Alert tone="critical">
      <Text>This job ad is no longer advertised.</Text>
    </Alert>
  );

  return (
    <ContentBlock width="large">
      <Box
        paddingTop={['xlarge', 'xxlarge']}
        paddingX={['none', 'large', 'xxlarge']}
        paddingBottom="none"
      >
        <Stack space="xlarge">
          <Box>
            <Heading level="1" align="center">
              Share your job ad with more people, in more places.
            </Heading>
          </Box>
          <Box paddingX={['none', 'xsmall', 'xxlarge']}>
            <Stack space="xxsmall" align="center">
              <>
                {loading && <Loader />}
                {error && renderInvalidJobErrorJSX}
                {jobExpired && renderExpiredJobErrorJSX}
                {jobTitle && !jobExpired && renderBodyWithJobTitle(jobTitle)}
              </>
              <Box
                className={styles.phones}
                style={{
                  backgroundImage: `url(${Phones})`,
                }}
              />
            </Stack>
          </Box>
        </Stack>
      </Box>
    </ContentBlock>
  );
};

export default PageIntro;
